// Capa de API del panel de reservas (backoffice). Thin wrappers sobre fetch con
// el header access-token, siguiendo el patrón del resto del backoffice.
// El backend responde { error, message, data }. Si error === 1002 (sesión inválida)
// redirige al login como hacen las otras páginas.

const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
  'access-token': localStorage.getItem('token'),
  'Content-Type': 'application/json',
});

async function request(path, options = {}) {
  const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
  const body = await res.json();
  if (body && body.error && body.error > 0) {
    if (body.error === 1002) {
      const home = localStorage.getItem('homePageURL');
      if (home) window.location.href = home;
    }
    // El backend manda el motivo específico en data.message (p.ej. "Sin disponibilidad...",
    // "Mesa ocupada"); el message de nivel superior es genérico por código (4000 = "La compra
    // no existe"), así que preferimos el de data cuando está.
    const detail = body.data && body.data.message;
    const err = new Error(detail || body.message || 'Ocurrió un error');
    err.code = body.error;
    err.body = body;
    throw err;
  }
  return body ? body.data : null;
}

const qs = (params) => {
  const entries = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&') : '';
};

// --- Lectura ---
export const getAgenda = (from, to) => request(`tables/agenda${qs({ from, to })}`);
// Sin flag: sólo mesas activas (lo que se ofrece para sentar). Con includeInactive: todas.
export const getTables = (includeInactive) => request(`tables${includeInactive ? '?includeInactive=1' : ''}`);
export const createTable = (payload) =>
  request('tables', { method: 'POST', body: JSON.stringify(payload) });
export const updateTable = (id, payload) =>
  request(`tables/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const listReservations = (date, status) => request(`reservations${qs({ date, status })}`);
// El detalle de una reserva de MESA ABIERTA viaja además con sus anotados
// (`openTableSignups`: las inscripciones de open_table_signup, con lugares, estado y pago).
// Se normaliza acá adentro de `reservation` para que la pantalla lea siempre el mismo lugar,
// venga colgado de la reserva o al lado de ella. Una reserva común no trae el campo.
export const getReservationDetail = async (id) => {
  const data = await request(`reservations/${id}`);
  const reservation = data && data.reservation;
  if (reservation && !Array.isArray(reservation.openTableSignups) && Array.isArray(data.openTableSignups)) {
    return { ...data, reservation: { ...reservation, openTableSignups: data.openTableSignups } };
  }
  return data;
};

// --- Acciones sobre una reserva (admin) ---
export const patchReservationStatus = (id, status) =>
  request(`reservations/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
export const releaseTable = (id) =>
  request(`reservations/${id}/release`, { method: 'PATCH' });

// Validar la llegada registrando cuánta gente vino DE VERDAD. Sin `playerCount` es el
// "validar llegada" de siempre (patchReservationStatus a 'seated').
export const checkInReservation = (id, playerCount) =>
  request(`tables/${id}/check-in`, { method: 'POST', body: JSON.stringify({ playerCount }) });
// Reprogramar: cambiar la FECHA y/o la HORA de una reserva que todavía no llegó. El backend
// conserva la mesa si sigue libre a esa hora y, si no, reasigna una y la devuelve en `table`
// (con `tableChanged`). No toca la cuenta: lo cobrado sigue cobrado.
export const rescheduleReservation = (id, date, startHour) =>
  request(`reservations/${id}/reschedule`, { method: 'PATCH', body: JSON.stringify({ date, startHour: Number(startHour) }) });

// Cambiar la reserva de mesa: la cuenta (entradas, comandas, juegos) viaja con ella.
// ENI-269: con `count` se mueven sólo esas personas (split/merge); sin él, la mesa entera.
export const moveReservation = (id, tableId, count) =>
  request(`tables/${id}/move`, { method: 'POST', body: JSON.stringify(count != null ? { tableId, count } : { tableId }) });

// --- Juegos de la sesión de mesa (F2) ---
export const addReservationGame = (id, payload) =>
  request(`tables/${id}/games`, { method: 'POST', body: JSON.stringify(payload) });
export const removeReservationGame = (id, gameId) =>
  request(`tables/${id}/games/${gameId}`, { method: 'DELETE' });

// --- Cuenta de la mesa (F3): seats + comandas, con cobros parciales ---
export const getTableAccount = (id) => request(`tables/${id}/account`);
// F1 del arqueo: los lectores de tarjeta activos de la sucursal.
export const getPosDevices = () => request('posDevices');

export const settleTableAccount = (id, payload) =>
  request(`tables/${id}/account/settle`, { method: 'POST', body: JSON.stringify(payload) });

// --- Eventos con consumo incluido ---
// "Pasar a incluido": la comanda impaga de una mesa de un evento deja de cobrarse y cuenta como
// entregado del evento. Responde { purchaseId, account, eventIncluded }.
export const includeOrder = (purchaseId) =>
  request(`cafeteria/sale/${purchaseId}/include`, { method: 'POST' });
// El menú entero de cafetería (todo lo no eliminado): la categoría de cada ítem, para el aviso
// previo de "Pasar a incluido".
export const getCafeteriaMenu = () => request('cafeteria/menu');

// --- Invitados ---
export const addGuests = (id, guests) =>
  request(`tables/${id}/guests`, { method: 'POST', body: JSON.stringify({ guests }) });
export const removeGuest = (id, guestId) =>
  request(`tables/${id}/guests/${guestId}`, { method: 'DELETE' });

// --- Disponibilidad + walk-in (F3) ---
export const checkAvailability = (date, startHour, playerCount) =>
  request(`reservations/available${qs({ date, startHour, playerCount })}`);
export const createWalkIn = (payload) =>
  request('tables/walk-in', { method: 'POST', body: JSON.stringify(payload) });
export const getDemoGames = () => request('games/demo');

// --- Ludoteca (ABM de qué juegos se pueden jugar en el local) ---
// `getDemoGames` (arriba) devuelve SÓLO lo jugable hoy: alimenta el combo del walk-in y el
// picker del storefront. Éste devuelve TODOS los juegos de mesa del catálogo, marcados o no,
// que es lo que hace falta para poder marcarlos.
export const getLibrary = () => request('games/demo/admin');
export const setLibraryStatus = (code, payload) =>
  request(`games/demo/${code}`, { method: 'PUT', body: JSON.stringify(payload) });

// Copias por dueño. `copies: 0` es baja lógica de esa copia (el dueño se la llevó) y, cuando el
// total del juego llega a 0, lo saca del picker de reservas aunque el juego siga marcado.
export const setGameCopies = (code, payload) =>
  request(`games/demo/${code}/copies`, { method: 'PUT', body: JSON.stringify(payload) });

// --- Dueños de las copias (la casa, proveedores, amigos que prestaron un juego) ---
export const getGameOwners = () => request('games/demo/owners');
// Reusa el endpoint de proveedores que YA existe (ENI-92). La ludoteca no tiene un listado
// propio a propósito: uno paralelo duplicaba la superficie y revivía el filtro laxo con
// 'general' que ENI-187 sacó porque devolvía todos los proveedores sin clasificar.
export const getGameProviders = () => request('provider/product?category=juegos');
export const createGameOwner = (payload) =>
  request('games/demo/owners', { method: 'POST', body: JSON.stringify(payload) });
export const updateGameOwner = (id, payload) =>
  request(`games/demo/owners/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
export const deleteGameOwner = (id) =>
  request(`games/demo/owners/${id}`, { method: 'DELETE' });

// Lookup de cliente por cédula (mismo que usa el alta de ventas). Devuelve {profile, account, ...}
// o lanza (code 4000) si no existe — el caller lo trata como "cliente nuevo".
export const getClientByCedula = (cedula) => request(`clients/${encodeURIComponent(cedula)}`);

// --- Horario + bloqueos (F4) ---
export const getSchedule = () => request('tables/settings');
export const saveSchedule = (values) =>
  request('tables/settings', { method: 'POST', body: JSON.stringify(values) });
export const getBlocks = (from, to) => request(`tables/blocks${qs({ from, to })}`);
export const addBlock = (payload) =>
  request('tables/blocks', { method: 'POST', body: JSON.stringify(payload) });
// ENI-221: editar en vez de crear otro. Sin esto, cambiarle el horario a un bloqueo dejaba el
// original vivo y la mesa seguía bloqueada en la franja vieja.
export const updateBlock = (id, payload) =>
  request(`tables/blocks/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const removeBlock = (id) =>
  request(`tables/blocks/${id}`, { method: 'DELETE' });

// --- Jornada del local (puede cruzar la medianoche) ---
// Con `closeHour > 24` (p. ej. 25 = cierra 01:00), a las 00:30 el local sigue abierto y la
// jornada es la de AYER: la agenda y las mesas sentadas hay que pedirlas con esa fecha, y la
// hora "de jornada" corre (00:30 -> 24). Espeja businessNow del backend.
// Horario vigente para una fecha: el global con el override del día de semana encima
// (p. ej. viernes/sábados hasta las 24). Espeja scheduleForDate del backend.
export function scheduleForDate(schedule, ymdStr) {
  if (!schedule) return {};
  const overrides = schedule.weekdayOverrides || {};
  const weekday = new Date(`${ymdStr}T12:00:00`).getDay();
  const dayOverride = overrides[String(weekday)];
  if (!dayOverride) return schedule;
  const merged = { ...schedule };
  ['openHour', 'closeHour', 'lastReservation'].forEach((key) => {
    if (dayOverride[key] !== undefined && dayOverride[key] !== null && dayOverride[key] !== '') {
      merged[key] = Number(dayOverride[key]);
    }
  });
  return merged;
}

export function businessNow(schedule) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const clockHour = now.getHours();
  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = ymd(yesterdayDate);
  // Quien puede seguir abierto es la jornada de AYER, con SU horario (un sábado que cierra 24
  // hace que el domingo a las 00:30 siga siendo sábado).
  const overnightUntil = Math.max(0, Number(scheduleForDate(schedule, yesterday).closeHour || 0) - 24);
  if (overnightUntil > 0 && clockHour < overnightUntil) {
    return { date: yesterday, hour: clockHour + 24, minute: now.getMinutes() };
  }
  return { date: ymd(now), hour: clockHour, minute: now.getMinutes() };
}

// --- Constantes UI ---
export const RESERVATION_STATUS = {
  pending:   { label: 'Pendiente',  badge: 'badge-warning' },
  active:    { label: 'Reservada',  badge: 'badge-success' },
  seated:    { label: 'En mesa',    badge: 'badge-info' },
  completed: { label: 'Liberada',   badge: 'badge-secondary' },
  cancelled: { label: 'Cancelada',  badge: 'badge-danger' },
};

// FR-011: origen de la reserva con etiqueta legible (nunca el enum crudo). 'abierta' es la
// reserva de una mesa abierta (Mesa Abierta): sin titular, sus lugares se venden de a uno.
// 'actividad' es una mesa que tomó una jornada del club: la creó y la libera el evento, no el
// salón (FR-076).
export const RESERVATION_ORIGIN_LABEL = {
  reserva:   'Reservada de antemano',
  walkin:    'Sin reserva (walk-in)',
  abierta:   'Mesa abierta',
  actividad: 'Mesa de una jornada',
};

/**
 * Quién manda sobre esta reserva: el salón, o la pantalla que la creó (FR-075, FR-076).
 *
 * Las dos familias "gestionadas" se comportan igual en Mesas y en el salón —el estado no se
 * cambia a mano desde la grilla, y hay una pantalla dueña a la que ir— así que las seis
 * comparaciones sueltas `origin === 'abierta'` se leen por acá y el origen nuevo entra en
 * todas a la vez.
 *
 * Cuelga de `origin`, NO de `r.activity`: el adjunto de la agenda es best-effort (si la query
 * falla el backend loguea y no manda nada), así que una reserva con `origin === 'actividad'`
 * y sin `activity` es un estado alcanzable — sigue siendo del club aunque no sepamos de qué
 * jornada.
 */
export const managedBy = (r) => {
  const origin = r && r.origin;
  if (origin === 'abierta') return 'abierta';
  if (origin === 'actividad') return 'actividad';
  return null;
};

// ENI-196: suma personas (= entradas) a una mesa ya abierta.
export const addReservationSeats = (id, payload) =>
  request(`tables/${id}/seats`, { method: 'POST', body: JSON.stringify(payload) });
// Espejo: quita personas. Lo que protege la plata es `paid_seats` en el backend, no el rol.
export const removeReservationSeats = (id, count) =>
  request(`tables/${id}/seats/remove`, { method: 'POST', body: JSON.stringify({ count }) });

export const PAYMENT_METHOD_LABEL = {
  efectivo:      'Efectivo',
  transferencia: 'Transferencia',
  mercadopago:   'MercadoPago',
  pos:           'POS',   // ENI-206: faltaba, y hoy 15 de 66 reservas de dev mostraban '—'
};

// Medios que se cobran con la persona presente. 'pos' es la posnet del mostrador: sin él, una
// reserva del backoffice con el cobro diferido caía en "Sin cobrar", que es el estado del no-show.
const A_COBRAR_EN_LOCAL = ['efectivo', 'transferencia', 'pos'];

// Medios con los que se puede cobrar una cuenta de mesa (ids de `ticket_enum`, los mismos que
// acepta `VALID_PAYMENT_TYPES` del backend). Vivía suelto en SalonView; ahora también lo usa el
// cobro desde el listado de reservas, y un diccionario de medios de pago duplicado es de los que
// se desincronizan callado.
export const SETTLE_PAYMENTS = [[1, 'Efectivo'], [5, 'POS'], [2, 'Transferencia'], [3, 'MercadoPago']];

/**
 * Lector de tarjeta preseleccionado al cobrar una mesa: el de cafetería, que es el del salón.
 * Queda visible para cambiarlo porque a veces la mesa se paga en el de la tienda — el caso que
 * descuadra el cierre de lote y el motivo de que el cobro registre por cuál aparato entró.
 *
 * Vive acá y no en SalonView porque son DOS pantallas las que cobran la cuenta de una mesa (el
 * salón y el detalle del listado): si cada una elige su default, terminan preseleccionando
 * lectores distintos para el mismo cobro y nadie se entera hasta conciliar el lote.
 */
export function pickDefaultPosDevice(devices) {
  if (!Array.isArray(devices) || devices.length === 0) return null;
  return devices.find((d) => /cafeter/i.test(d.name)) || devices[0];
}

// El medio DECLARADO al reservar (`reservations.payment_method`) traducido al id de ticket con
// el que se cobra. Es sólo un default para el selector: lo que termina en el ticket es lo que el
// mostrador elige, porque quien reservó "efectivo" bien puede terminar transfiriendo.
export const PAYMENT_METHOD_TO_TYPE = {
  efectivo:      1,
  transferencia: 2,
  mercadopago:   3,
  pos:           5,
};

/**
 * R-14 — Importe de cobrar los lugares [from, to) de una reserva: ESPEJO EXACTO de
 * `seatsChargeAmount` del backend (ReservationController), que es quien termina cobrando.
 *
 * No se puede mostrar `personas × cover vigente`: desde ENI-196 `total_amount` acumula a los
 * precios de cada momento y, en una mesa abierta, cada inscripción entra al precio de SU mesa
 * (TD-09) — que puede no ser el cover del salón. Con precio distinto el panel del salón decía
 * "faltan $500" contra una cuenta de $600, y en una mesa abierta gratis inventaba deuda sobre
 * una cuenta en cero.
 *
 * El reparto se hace por diferencias de acumulados redondeados (igual que el backend), así que
 * la suma de todos los lugares da el total EXACTO y el resto del redondeo lo absorbe el último.
 */
export function seatsChargeAmount(reservation, from, to) {
  const players = Number(reservation.player_count) || 0;
  const total   = Number(reservation.total_amount) || 0;
  // Lugares de socio sin cargo (backend docs/spec-membresia-mesas.md): son las PRIMERAS
  // `member_seats` posiciones, valen $0 y ya están contados en `paid_seats`. El total se
  // prorratea sólo entre los que pagan.
  const member  = Math.max(0, Math.min(Number(reservation.member_seats) || 0, players));
  const chargeable = players - member;
  if (chargeable <= 0) return 0;
  const clamp = (k) => Math.max(0, Math.min((Number(k) || 0) - member, chargeable));
  const share = (k) => Math.round((total * clamp(k)) / chargeable);
  return Math.max(0, share(to) - share(from));
}

/**
 * Lugares de una reserva separados en lo que paga y lo que es de socios. `paid_seats` del
 * backend INCLUYE a los socios (son lugares saldados a $0): para hablar de plata hay que restarlos.
 */
export function seatBreakdown(reservation) {
  const players = Number(reservation && reservation.player_count) || 0;
  const members = Math.min(players, Number(reservation && reservation.member_seats) || 0);
  // Lugares cubiertos por un voucher de mesa (premio o cortesía): saldados a $0 dentro de
  // `paid_seats`, igual que los de socio. No son entradas pagas ni a pagar.
  const vouchers = Math.min(players - members, Number(reservation && reservation.voucher_seats) || 0);
  const paid    = Math.max(0, (Number(reservation && reservation.paid_seats) || 0) - members - vouchers);
  return { players, members, vouchers, paying: players - members - vouchers, paid };
}

// --- Socios en la mesa (lugar sin cargo por membresía) ---
// Con el beneficio suma una persona gratis. Sin él el backend responde 1001 con
// `data.canAddPaying`: el mostrador puede repetir con `pay: true` y entra pagando su entrada.
export const addMemberSeat = (id, { cedula, pay = false }) =>
  request(`tables/${id}/member-seats`, { method: 'POST', body: JSON.stringify({ cedula, pay }) });
export const removeMemberSeat = (id, seatId) =>
  request(`tables/${id}/member-seats/${seatId}`, { method: 'DELETE' });

// Voucher de mesa (backend docs/spec-premios-cortesias-vouchers.md FR-105): "Mesa para N" cubre N
// lugares a $0 y, si la mesa tiene menos, le suma lugares hasta N.
// → { purchaseId, seats, addedSeats, listValue, label }.
export const applyTableVoucher = (id, code) =>
  request(`tables/${id}/voucher`, { method: 'POST', body: JSON.stringify({ code }) });

/**
 * ¿Se le pueden cobrar entradas a esta reserva desde el detalle?
 *
 * El caso que abre esta puerta: el cliente reserva, transfiere ANTES de venir (el propio
 * backoffice le da los datos bancarios al reservar con medio 'transferencia') y hasta ahora no
 * había dónde registrar esa plata — la cuenta de la mesa sólo se abre desde el salón, y sólo
 * sobre una mesa YA SENTADA. La reserva quedaba "Impaga · A cobrar en el local" hasta que la
 * persona llegaba, con el riesgo de cobrarle dos veces.
 *
 * - Mesa abierta: no. Sus entradas se cobran por inscripción, desde su propia pantalla.
 * - Cover gratis: no hay entradas que cobrar (el backend lo rechaza igual, ENI-268).
 * - 'pending' queda afuera a propósito: es una reserva esperando el pago de MercadoPago.
 *   Cobrarla acá la dejaría paga y pendiente a la vez; primero se pasa a Activa.
 */
export function canChargeSeats(r) {
  if (!r || r.origin === 'abierta') return false;
  if (Number(r.cover_free) === 1) return false;
  if (r.status !== 'active' && r.status !== 'seated') return false;
  return (Number(r.player_count) || 0) > (Number(r.paid_seats) || 0);
}

/**
 * ¿Se le puede cambiar la fecha/hora a esta reserva?
 *
 * Sólo un plan se reprograma: 'active' y 'pending' (esta última sigue esperando el pago de
 * MercadoPago, pero es igual de futura). Una mesa ya sentada está ocurriendo — su hora es un
 * hecho — y completada/cancelada son terminales.
 *
 * Mesa abierta: no por acá. Su fecha y su hora son las que vieron y pagaron los anotados, y
 * este camino no les avisa nada (mismo criterio que el estado de sólo lectura del detalle).
 * El backend rechaza los dos casos igual; esto es para no ofrecer un botón que va a rebotar.
 */
export function canReschedule(r) {
  if (!r || r.origin === 'abierta') return false;
  return r.status === 'active' || r.status === 'pending';
}

/**
 * ENI-206 — Estado de cobro de las entradas de una reserva.
 *
 * `paid_seats` son las entradas FACTURADAS (venta + ticket + movimiento de caja), no plata
 * en la mano. ENI-222: hasta ese fix una reserva online en efectivo/transferencia nacía con
 * paid_seats = player_count sin que nadie hubiera pagado, y esta función lo tapaba con un hint.
 * Ahora nace en 0 y el caso tiene su propio estado: "Impaga · A cobrar en el local".
 *
 * Un no-show NO debe la entrada: el backend sólo cuenta lugares impagos si la mesa se usó
 * (getOutstandingBalance). Con la regla literal "paid_seats === 0 ⇒ impaga", 31 de las 66
 * reservas de dev se pintaban de rojo sin deber nada.
 */
export function paymentState(reservation) {
  // ENI-268: mesa gratis — no hay entradas que cobrar, ni pagas ni impagas. Va primero:
  // cualquier regla de abajo la pintaría como deuda (paid_seats queda en 0 a propósito).
  if (Number(reservation.cover_free) === 1) {
    return { key: 'free', label: 'Gratis', badge: 'badge-info', hint: null };
  }
  // Los socios sin cargo no son plata: el estado de pago mira sólo los lugares que pagan.
  const { members, vouchers, paying: players, paid } = seatBreakdown(reservation);
  if (members > 0 && players === 0 && vouchers === 0) {
    return { key: 'member', label: 'Socio', badge: 'badge-info', hint: null };
  }
  // Toda la mesa la cubrió un voucher (premio o cortesía): no debe nada y no pagó nada.
  if (vouchers > 0 && players === 0) {
    return { key: 'voucher', label: 'Voucher', badge: 'badge-info', hint: null };
  }
  const usada   = reservation.status === 'seated' || reservation.status === 'completed' || reservation.seated_at != null;

  // ENI-222: "Paga" ahora quiere decir que la plata ENTRÓ, y nada más. Antes se le colgaba el
  // hint "A cobrar en el local" para tapar que ENI-117 marcaba pagas las reservas online en
  // efectivo/transferencia sin cobro; arreglado eso en el backend, el hint sobra acá y encima
  // mentía en el walk-in en efectivo, donde el cobro ya ocurrió en el mostrador.
  if (players > 0 && paid >= players) {
    return { key: 'paid', label: 'Paga', badge: 'badge-success', hint: null };
  }
  if (paid > 0) return { key: 'partial', label: 'Parcial', badge: 'badge-warning', hint: `${paid}/${players}` };
  // Reserva confirmada que se abona al llegar: no está paga, pero tampoco es una deuda — es lo
  // que el mostrador tiene que cobrar cuando la persona aparezca. Se distingue del no-show
  // ("Sin cobrar"), que no debe nada porque nunca usó la mesa.
  const aCobrarEnLocal = !usada
    && (reservation.status === 'active' || reservation.status === 'seated')
    && A_COBRAR_EN_LOCAL.includes(reservation.payment_method);
  if (aCobrarEnLocal) {
    return { key: 'due', label: 'Impaga', badge: 'badge-warning', hint: 'A cobrar en el local' };
  }
  if (!usada) return { key: 'none', label: 'Sin cobrar', badge: 'badge-light', hint: null };
  return { key: 'unpaid', label: 'Impaga', badge: 'badge-danger', hint: `0/${players}` };
}
